import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Thermometer, X } from 'lucide-react';
import { TO_API, fmtDay } from '../TimeOff';
import { DatePickerField } from '../DatePickerField';

const STATUS_STYLE = {
  submitted: 'bg-amber-100 text-amber-800', hr_reviewed: 'bg-blue-100 text-blue-800', approved: 'bg-emerald-100 text-emerald-800',
  denied: 'bg-rose-100 text-rose-800', cancelled: 'bg-slate-100 text-slate-600',
};
export const STATUS_LABEL = { submitted: 'Sent to HR', hr_reviewed: 'With manager', approved: 'Approved', denied: 'Not approved', cancelled: 'Cancelled' };
const TYPE_LABEL = { vacation: 'Vacation', personal: 'Personal day', unpaid: 'Unpaid leave' };

export function RequestRow({ r, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 bg-white rounded-lg border border-slate-200 p-3" data-testid={`to-request-${r.id}`}>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-900">
          {TYPE_LABEL[r.type] || r.type}: {r.date_from === r.date_to ? fmtDay(r.date_from) : `${fmtDay(r.date_from)} → ${fmtDay(r.date_to)}`}
          <span className="text-slate-500 font-normal"> · {r.days} working day{r.days !== 1 ? 's' : ''}</span>
        </p>
        {r.note && <p className="text-xs text-slate-600 mt-0.5">"{r.note}"</p>}
        <p className="text-xs text-slate-500 mt-0.5">
          <span className={`px-1.5 py-0.5 rounded font-semibold ${STATUS_STYLE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
          {r.manager_name && r.status === 'hr_reviewed' && <> · waiting on {r.manager_name}</>}
          {r.decided_by && (r.status === 'approved' || r.status === 'denied') && <> · by {r.decided_by}{r.decision_note ? ` — "${r.decision_note}"` : ''}</>}
          {r.balance_warning && r.status !== 'denied' && <span className="text-rose-600"> · ⚠ over entitlement</span>}
        </p>
      </div>
      {children && <div className="flex flex-wrap items-center gap-1.5 sm:shrink-0 sm:justify-end sm:max-w-[55%] pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">{children}</div>}
    </div>
  );
}

export default function MyTimeOff({ me, onChanged }) {
  const b = me.balance;
  const [form, setForm] = useState({ date_from: '', date_to: '', type: 'vacation', note: '' });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sickOpen, setSickOpen] = useState(() => new URLSearchParams(window.location.search).get('sick') === '1');
  const [sickNote, setSickNote] = useState('');

  const submit = async () => {
    if (!form.date_from) return toast.error('Pick the first day');
    setBusy(true);
    try {
      const r = await axios.post(`${TO_API}/requests`, { ...form, date_to: form.date_to || form.date_from });
      toast.success('Request sent to HR — you\'ll get an email once it\'s approved');
      if (r.data.warning) toast.warning(r.data.warning, { duration: 9000 });
      setOpen(false); setForm({ date_from: '', date_to: '', type: 'vacation', note: '' }); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send'); }
    finally { setBusy(false); }
  };
  const cancel = async (r) => {
    if (!window.confirm('Withdraw this request?')) return;
    try { await axios.post(`${TO_API}/requests/${r.id}/cancel`, {}); toast.success('Withdrawn'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not cancel'); }
  };
  const reportSick = async () => {
    setBusy(true);
    try {
      await axios.post(`${TO_API}/sick`, { note: sickNote });
      toast.success('Logged — HR and the foremen have been notified. Feel better.');
      setSickOpen(false); setSickNote(''); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not log it'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[['Entitlement', b.total, `${b.vacation_days} + ${b.carry_over} carried`], ['Used', b.used, 'approved days'], ['Pending', b.pending, 'awaiting approval'],
          ['Remaining', b.remaining - b.pending, 'after pending'], ['Sick days', b.sick_used, `in ${b.year}`]].map(([l, v, s]) => (
          <div key={l} className="bg-white rounded-xl border border-slate-200 p-3" data-testid={`to-stat-${l.toLowerCase().replace(' ', '-')}`}>
            <p className="text-xs text-slate-500">{l}</p>
            <p className={`text-2xl font-bold ${l === 'Remaining' && v < 0 ? 'text-rose-600' : 'text-slate-900'}`}>{v}</p>
            <p className="text-[11px] text-slate-500">{s}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => { setOpen(v => !v); setSickOpen(false); }} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1" data-testid="to-request-btn"><Plus className="w-4 h-4" /> Request time off</button>
        <button onClick={() => { setSickOpen(v => !v); setOpen(false); }} className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium inline-flex items-center gap-1" data-testid="to-sick-btn"><Thermometer className="w-4 h-4" /> I'm sick today</button>
      </div>
      {open && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_2fr_auto] gap-3 items-end" data-testid="to-request-form">
          <div className="text-xs text-slate-600">First day<DatePickerField value={form.date_from} testid="to-req-from" placeholder="Tap to pick a day"
            onChange={v => setForm(f => ({ ...f, date_from: v, date_to: !f.date_to || (v && f.date_to < v) ? v : f.date_to }))}
            className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white" /></div>
          <div className="text-xs text-slate-600">Last day<DatePickerField value={form.date_to} min={form.date_from} testid="to-req-to" placeholder="Same as first day"
            onChange={v => setForm(f => ({ ...f, date_to: v }))}
            className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white" /></div>
          <label className="text-xs text-slate-600">Type<select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid="to-req-type">
            <option value="vacation">Vacation</option><option value="personal">Personal day</option><option value="unpaid">Unpaid leave</option></select></label>
          <label className="text-xs text-slate-600">Note (optional)<input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="e.g. family trip" className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid="to-req-note" /></label>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50" data-testid="to-req-submit">{busy ? 'Sending…' : 'Send to HR'}</button>
          <p className="md:col-span-5 text-[11px] text-slate-500">Weekends and stat holidays aren't counted. HR reviews it first, then your manager approves — you get an email either way.</p>
        </div>
      )}
      {sickOpen && (
        <div className="bg-white rounded-xl border border-rose-200 p-4 flex flex-col md:flex-row gap-3 md:items-end" data-testid="to-sick-form">
          <label className="text-xs text-slate-600 flex-1">Anything the office should know? (optional)<input value={sickNote} onChange={e => setSickNote(e.target.value)} placeholder="e.g. back tomorrow" className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid="to-sick-note" /></label>
          <button onClick={reportSick} disabled={busy} className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium disabled:opacity-50" data-testid="to-sick-submit">{busy ? 'Logging…' : 'Log today as sick'}</button>
        </div>
      )}
      <div className="space-y-2">
        <h3 className="text-slate-900 font-semibold">My requests</h3>
        {me.requests.length === 0 && <p className="text-slate-700 text-sm">No requests yet.</p>}
        {me.requests.map(r => (
          <RequestRow key={r.id} r={r}>
            {['submitted', 'hr_reviewed'].includes(r.status) && (
              <button onClick={() => cancel(r)} className="p-1.5 rounded text-slate-500 hover:bg-slate-100" title="Withdraw" data-testid={`to-cancel-${r.id}`}><X className="w-4 h-4" /></button>
            )}
          </RequestRow>
        ))}
      </div>
    </div>
  );
}
